import { useState } from 'react';
import { apiFetch } from '@/lib/apiClient';
import { SetupScreenStyles } from './SetupScreenStyles';

interface WorkspaceNameSetupScreenProps {
  /** Active session token (the user is authenticated by the time this gate shows). */
  token: string | null;
  /** The workspace to name — the currently-selected container the gate fired on. */
  workspaceId?: string;
  /**
   * Prefilled into the name field so the field is never blank — the workspace's
   * current name (e.g. an upgraded solo space already named "{First}'s Workspace")
   * or a derived default. One tap to accept, or type a real (e.g. firm) name.
   */
  initialWorkspaceName?: string;
  /** True for team workspaces — adjusts the copy/placeholders. */
  isTeam?: boolean;
  /** Called once the name is saved (skipped=false) or the user skips (skipped=true). */
  onDone: (skipped: boolean) => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 13px',
  border: '1px solid var(--color-border, #e8e9e3)',
  borderRadius: 9,
  fontSize: 15,
  fontFamily: 'inherit',
  background: 'var(--color-surface, #fff)',
  color: 'var(--color-text-primary, #2b2f27)',
  outline: 'none',
  boxSizing: 'border-box',
};

/**
 * First-run gate for an un-named TEAM workspace. The naming gate
 * (`workspace-name-setup-gate.ts`) only fires for a team whose owner hasn't
 * named it yet (`nameSetByUser !== true`) — solo workspaces are auto-named at
 * creation (`{First}'s Workspace`) and never reach this screen. It captures
 * just the workspace name and renames the SELECTED workspace via
 * `PATCH /auth/profile { container_name, container_id }` (the `container_id`
 * targets that specific owned workspace rather than the user's oldest owned
 * one). Skippable so it never hard-blocks; a skip leaves the current name.
 * Mounted as a gate in App.tsx.
 */
export function WorkspaceNameSetupScreen({
  token,
  workspaceId,
  initialWorkspaceName = '',
  isTeam = false,
  onDone,
}: WorkspaceNameSetupScreenProps) {
  const [workspace, setWorkspace] = useState(initialWorkspaceName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = workspace.trim().length > 0 && !saving;

  const handleContinue = async () => {
    if (!canSubmit) return;
    if (!token) {
      setError('Your session expired — please refresh and try again.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Rename the selected workspace. `container_id` targets it specifically;
      // without it the server would rename the user's oldest owned workspace.
      await apiFetch('/auth/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        authToken: token,
        body: JSON.stringify({
          container_name: workspace.trim(),
          container_id: workspaceId,
        }),
      });
      onDone(false);
    } catch {
      setError("Couldn't save that — please try again.");
      setSaving(false);
    }
  };

  return (
    <div className="setup-screen">
      <div className="setup-content">
        <div className="setup-logo" aria-hidden="true">
          <div className="setup-logo-glow" />
          <img src="/favicon.svg" alt="" className="setup-logo-img" />
        </div>
        <h2>{isTeam ? 'Name your team workspace' : 'Name your workspace'}</h2>
        <p style={{ margin: '0 0 22px', color: 'var(--color-text-muted, #6b7167)', fontSize: 15, lineHeight: 1.5 }}>
          What should we call this space?
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 360, margin: '0 auto', textAlign: 'left' }}>
          <input
            type="text"
            value={workspace}
            onChange={(e) => { setWorkspace(e.target.value); setError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) void handleContinue(); }}
            placeholder={isTeam ? 'Workspace name (e.g. Acme Team)' : "Workspace name (e.g. Sam's Workspace)"}
            autoFocus
            style={inputStyle}
          />
          <button
            type="button"
            onClick={handleContinue}
            disabled={!canSubmit}
            style={{
              marginTop: 4,
              padding: '12px 16px',
              borderRadius: 10,
              border: 'none',
              background: 'var(--color-brand, #15a5e3)',
              color: '#fff',
              fontSize: 15,
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: canSubmit ? 'pointer' : 'default',
              opacity: canSubmit ? 1 : 0.5,
              transition: 'opacity 0.15s',
            }}
          >
            {saving ? 'Saving…' : 'Continue'}
          </button>
          {error && (
            <p style={{ margin: 0, color: 'var(--color-danger, #EA4335)', fontSize: 13 }}>{error}</p>
          )}
          <button
            type="button"
            onClick={() => onDone(true)}
            disabled={saving}
            style={{
              alignSelf: 'center',
              marginTop: 2,
              background: 'none',
              border: 'none',
              padding: '4px 8px',
              fontFamily: 'inherit',
              fontSize: 13,
              color: 'var(--color-text-muted, #6b7167)',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Skip for now
          </button>
        </div>
      </div>
      <SetupScreenStyles />
    </div>
  );
}
